'use client';

import { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { useUnit } from 'effector-react';
import { Socket } from 'socket.io-client';
import { axiosClient, socketClient } from '@nx-frontend/shared/api';
import { generalEnv } from '@nx-frontend/shared/env';
import { $currentOrganization } from '@nx-frontend/shared/store/current-organization/current-organization';
import { $currentWorkspace } from '@nx-frontend/shared/store/current-workspace';
import { $opCanAccessWebnotes } from '@nx-frontend/shared/store/org-role';
import { $workspaceId } from '@nx-frontend/shared/store/portal-app';
import { $session } from '@nx-frontend/shared/store/session';
import { $user } from '@nx-frontend/shared/store/user';
import { $workspacesIds } from '@nx-frontend/shared/store/workspaces';
import { OrganizationPrivilege, PortalServicePortalDto } from '@nx-frontend/shared/types';
import { RoleWithClient } from '@nx-frontend/shared/types';
import { NoAccessLayout } from './noaccess-layout';

export let socketIO: Socket | null = null;
export const SocketContext = createContext<Socket | null>(socketIO);

export function SocketProvider({ children, portalsClient }: { children: React.ReactNode; portalsClient?: boolean }) {
  const [
    currentWorkspace,
    portalSettingsWorkspace,
    opCanAccessWebnotes,
    workspacesIds,
    user,
    currentOrganization,
    session,
  ] = useUnit([
    $currentWorkspace,
    $workspaceId,
    $opCanAccessWebnotes,
    $workspacesIds,
    $user,
    $currentOrganization,
    $session,
  ]);
  const [showNoAccess, setShowNoAccess] = useState(false);
  const [userRole, setUserRole] = useState('');
  const [portal, setPortal] = useState<PortalServicePortalDto | null>(null);

  const currentWorkspaceId = useMemo(() => {
    return currentWorkspace.globalId || portalSettingsWorkspace;
  }, [currentWorkspace.globalId, portalSettingsWorkspace]);

  const [socket, setSocket] = useState<Socket | null>(null);

  const setSocketHandler = useCallback(() => {
    if (currentWorkspaceId) {
      socketIO?.emit('socketConnect:connectWorkspace', currentWorkspaceId);
    }
    setSocket(socketIO);
  }, [currentWorkspaceId]);

  useEffect(() => {
    const checkAccess = async () => {
      if (!portalsClient && typeof window !== 'undefined' && user.id && !opCanAccessWebnotes) {
        try {
          const { value: role } = (
            await axiosClient.post<{ value: RoleWithClient }>(`/v2/api/users/role`, {
              orgId: currentOrganization?.globalId,
            })
          ).data;
          if ('client' === role.role) {
            const portal = (
              await axiosClient.get<PortalServicePortalDto | null>(
                `/v2/api/workspaces/${currentWorkspaceId}/portal/find`
              )
            ).data;
            setPortal(portal);
          }
          setUserRole(role.role);
          if (!role.privileges.includes(OrganizationPrivilege.CAN_ACCESS_WEBNOTES)) {
            setShowNoAccess(true);
          } else {
            setShowNoAccess(false);
          }
        } catch (err) {
          setShowNoAccess(true);
        }
      } else {
        setShowNoAccess(false);
      }
    };
    checkAccess();
  }, [portalsClient, user.id, opCanAccessWebnotes, currentOrganization?.globalId, currentWorkspaceId]);

  useEffect(() => {
    if (!portalsClient || (session.userId !== undefined && user.id !== 1)) {
      if (!socketIO && (currentWorkspace || portalSettingsWorkspace)) {
        socketIO = socketClient({
          socketServer: {
            transports: ['websocket'],
            url: 'local' === generalEnv.env ? generalEnv.localSocketProxy : window.location.hostname,
          },
          availableWorkspaces: [currentWorkspace.globalId || portalSettingsWorkspace, ...workspacesIds],
        });

        socketIO.on('connect', () => {
          setSocketHandler();
        });
      } else if (socketIO) {
        setSocketHandler();
      }
    }
  }, [
    currentWorkspace,
    portalSettingsWorkspace,
    workspacesIds,
    currentWorkspaceId,
    setSocketHandler,
    session,
    portalsClient,
    user.id,
  ]);
  return (
    <SocketContext.Provider value={socket}>
      {showNoAccess ? <NoAccessLayout userRole={userRole} portal={portal} /> : children}
    </SocketContext.Provider>
  );
}
